/**
 * Dettaglio richiesta di preventivo (RDP) con righe, invii a fornitori
 * e pannello confronto preventivi.
 *
 * Flusso:
 * - Visualizza righe della richiesta
 * - Invia la richiesta a uno o piu fornitori
 * - Sollecita fornitori che non hanno risposto
 * - Collega preventivi ricevuti agli invii
 * - Confronta preventivi quando almeno 2 invii hanno un preventivo collegato
 */

import React, { useCallback, useEffect, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { Button, Card, Input, Label } from '@floraltech/ui';
import { useFormatters } from '@lib/formatters';
import { StatoRichiestaBadge } from '../components/StatoRichiestaBadge';
import { ConfrontoPreventiviPanel } from '../components/ConfrontoPreventiviPanel';

interface RigaRichiesta {
  id: string;
  richiesta_id: string;
  articolo_id: string | null;
  codice_articolo: string | null;
  descrizione: string;
  unita_misura: string | null;
  quantita: string;
  note: string | null;
}

interface Invio {
  id: string;
  richiesta_id: string;
  fornitore_id: string;
  rappresentante_id: string | null;
  email_destinatario: string | null;
  stato_invio: string;
  data_invio: string | null;
  data_risposta: string | null;
  preventivo_id: string | null;
  note: string | null;
}

interface RichiestaDetail {
  id: string;
  tenant_id: string;
  numero: string;
  anno: number;
  stato: string;
  data_richiesta: string;
  data_scadenza_risposta: string | null;
  valuta: string;
  note: string | null;
  righe: RigaRichiesta[];
  invii: Invio[];
}

interface Fornitore {
  id: string;
  ragione_sociale: string | null;
  nome: string | null;
  cognome: string | null;
}

interface PreventivoRef {
  id: string;
  numero: string;
  stato: string;
  data_preventivo: string;
}

const STATI_INVIO_BADGE: Record<string, { label: string; className: string }> = {
  da_inviare: { label: 'Da inviare', className: 'bg-muted text-muted-foreground' },
  inviata: { label: 'Inviata', className: 'bg-blue-100 text-blue-800 dark:bg-blue-900/30 dark:text-blue-300' },
  sollecitata: { label: 'Sollecitata', className: 'bg-amber-100 text-amber-800 dark:bg-amber-900/30 dark:text-amber-300' },
  risposta: { label: 'Risposta', className: 'bg-green-100 text-green-800 dark:bg-green-900/30 dark:text-green-300' },
};

export function RichiestaDetail() {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const { date, qty } = useFormatters();
  const token = localStorage.getItem('token');

  const [richiesta, setRichiesta] = useState<RichiestaDetail | null>(null);
  const [loading, setLoading] = useState(true);
  const [actionLoading, setActionLoading] = useState(false);

  // Invio a nuovo fornitore
  const [fornitori, setFornitori] = useState<Fornitore[]>([]);
  const [invioFornitoreId, setInvioFornitoreId] = useState('');
  const [invioEmail, setInvioEmail] = useState('');

  // Collega preventivo
  const [collegaInvioId, setCollegaInvioId] = useState<string | null>(null);
  const [preventiviDisponibili, setPreventiviDisponibili] = useState<PreventivoRef[]>([]);
  const [collegaPreventivoId, setCollegaPreventivoId] = useState('');

  const fetchData = useCallback(async () => {
    setLoading(true);
    const res = await fetch(`/api/v1/richieste/${id}`, {
      headers: { Authorization: `Bearer ${token}` },
    });
    if (res.ok) setRichiesta(await res.json());
    setLoading(false);
  }, [id, token]);

  // Carica fornitori per il form invio
  const fetchFornitori = useCallback(async () => {
    const res = await fetch('/api/v1/soggetti?tipo=fornitore&limit=200', {
      headers: { Authorization: `Bearer ${token}` },
    });
    if (res.ok) {
      const data = await res.json();
      setFornitori(Array.isArray(data) ? data : data.items ?? []);
    }
  }, [token]);

  useEffect(() => {
    fetchData();
    fetchFornitori();
  }, [fetchData, fetchFornitori]);

  const nomeFornitore = (f: Fornitore): string =>
    f.ragione_sociale || [f.nome, f.cognome].filter(Boolean).join(' ') || '—';

  const fornitoreName = (fornitoreId: string): string => {
    const f = fornitori.find((x) => x.id === fornitoreId);
    return f ? nomeFornitore(f) : fornitoreId.substring(0, 8) + '...';
  };

  const handleInviaRichiesta = async () => {
    if (!richiesta) return;
    setActionLoading(true);
    await fetch(`/api/v1/richieste/${richiesta.id}/invia`, {
      method: 'POST',
      headers: {
        Authorization: `Bearer ${token}`,
        'Content-Type': 'application/json',
      },
      body: JSON.stringify({}),
    });
    await fetchData();
    setActionLoading(false);
  };

  const handleAnnulla = async () => {
    if (!richiesta) return;
    setActionLoading(true);
    await fetch(`/api/v1/richieste/${richiesta.id}/annulla`, {
      method: 'POST',
      headers: { Authorization: `Bearer ${token}` },
    });
    await fetchData();
    setActionLoading(false);
  };

  const handleInviaAFornitore = async () => {
    if (!richiesta || !invioFornitoreId) return;
    setActionLoading(true);
    const body: Record<string, unknown> = { fornitore_id: invioFornitoreId };
    if (invioEmail.trim()) body.email_destinatario = invioEmail.trim();

    await fetch(`/api/v1/richieste/${richiesta.id}/invia`, {
      method: 'POST',
      headers: {
        Authorization: `Bearer ${token}`,
        'Content-Type': 'application/json',
      },
      body: JSON.stringify(body),
    });
    setInvioFornitoreId('');
    setInvioEmail('');
    await fetchData();
    setActionLoading(false);
  };

  const handleSollecita = async (invioId: string) => {
    if (!richiesta) return;
    setActionLoading(true);
    await fetch(`/api/v1/richieste/${richiesta.id}/invii/${invioId}/sollecita`, {
      method: 'POST',
      headers: { Authorization: `Bearer ${token}` },
    });
    await fetchData();
    setActionLoading(false);
  };

  const handleOpenCollega = async (invio: Invio) => {
    setCollegaInvioId(invio.id);
    setCollegaPreventivoId('');
    // Carica preventivi del fornitore per l'anno della richiesta
    const params = new URLSearchParams({
      fornitore_id: invio.fornitore_id,
      anno: String(richiesta?.anno ?? new Date().getFullYear()),
    });
    const res = await fetch(`/api/v1/preventivi?${params}`, {
      headers: { Authorization: `Bearer ${token}` },
    });
    if (res.ok) {
      setPreventiviDisponibili(await res.json());
    }
  };

  const handleCollegaPreventivo = async () => {
    if (!richiesta || !collegaInvioId || !collegaPreventivoId) return;
    setActionLoading(true);
    await fetch(
      `/api/v1/richieste/${richiesta.id}/invii/${collegaInvioId}/collega-preventivo`,
      {
        method: 'POST',
        headers: {
          Authorization: `Bearer ${token}`,
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({ preventivo_id: collegaPreventivoId }),
      },
    );
    setCollegaInvioId(null);
    setCollegaPreventivoId('');
    setPreventiviDisponibili([]);
    await fetchData();
    setActionLoading(false);
  };

  if (loading || !richiesta) {
    return <div className="p-6 text-muted-foreground">Caricamento...</div>;
  }

  const isEditable = richiesta.stato === 'bozza' || richiesta.stato === 'inviata';
  const inviiConPreventivo = richiesta.invii.filter((i) => i.preventivo_id);
  const showConfronto = inviiConPreventivo.length >= 2;

  return (
    <div className="p-6 space-y-6 w-full">
      {/* Header */}
      <div className="flex items-start justify-between">
        <div>
          <h1 className="text-xl font-outfit font-semibold text-foreground">
            Richiesta {richiesta.numero}
          </h1>
          <div className="flex items-center gap-3 mt-1">
            <StatoRichiestaBadge stato={richiesta.stato} />
            <span className="text-sm text-muted-foreground">
              {date(richiesta.data_richiesta)}
            </span>
            {richiesta.data_scadenza_risposta && (
              <span className="text-sm text-muted-foreground">
                Scadenza: {date(richiesta.data_scadenza_risposta)}
              </span>
            )}
          </div>
          {richiesta.note && (
            <p className="text-sm text-muted-foreground mt-1">{richiesta.note}</p>
          )}
        </div>

        <div className="flex gap-2">
          {richiesta.stato === 'bozza' && (
            <Button
              onClick={handleInviaRichiesta}
              disabled={actionLoading || richiesta.righe.length === 0}
            >
              Invia
            </Button>
          )}
          {isEditable && (
            <Button
              variant="destructive"
              onClick={handleAnnulla}
              disabled={actionLoading}
            >
              Annulla
            </Button>
          )}
        </div>
      </div>

      {/* Righe */}
      <Card className="overflow-hidden">
        <div className="px-4 py-2 border-b border-border bg-muted/50">
          <h2 className="text-sm font-semibold text-foreground">Righe richiesta</h2>
        </div>
        {richiesta.righe.length === 0 ? (
          <div className="p-8 text-center text-muted-foreground">
            Nessuna riga
          </div>
        ) : (
          <table className="w-full text-sm">
            <thead>
              <tr className="border-b border-border">
                <th className="text-left px-4 py-2 text-muted-foreground font-medium">Codice</th>
                <th className="text-left px-4 py-2 text-muted-foreground font-medium">Descrizione</th>
                <th className="text-left px-4 py-2 text-muted-foreground font-medium">U.M.</th>
                <th className="text-right px-4 py-2 text-muted-foreground font-medium">Quantita</th>
                <th className="text-left px-4 py-2 text-muted-foreground font-medium">Note</th>
              </tr>
            </thead>
            <tbody>
              {richiesta.righe.map((r) => (
                <tr key={r.id} className="border-b border-border">
                  <td className="px-4 py-2 font-mono text-xs">{r.codice_articolo || '—'}</td>
                  <td className="px-4 py-2">{r.descrizione}</td>
                  <td className="px-4 py-2">{r.unita_misura || '—'}</td>
                  <td className="px-4 py-2 text-right tabular-nums">{qty(r.quantita)}</td>
                  <td className="px-4 py-2 text-muted-foreground">{r.note || '—'}</td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </Card>

      {/* Invii a fornitori */}
      <Card className="p-4 space-y-4">
        <h2 className="text-sm font-semibold text-foreground">Invii a fornitori</h2>

        {richiesta.invii.length > 0 && (
          <div className="border border-border rounded overflow-hidden">
            <table className="w-full text-sm">
              <thead>
                <tr className="border-b border-border bg-muted/50">
                  <th className="text-left px-3 py-1.5 text-muted-foreground font-medium">Fornitore</th>
                  <th className="text-left px-3 py-1.5 text-muted-foreground font-medium">Email</th>
                  <th className="text-left px-3 py-1.5 text-muted-foreground font-medium">Stato</th>
                  <th className="text-left px-3 py-1.5 text-muted-foreground font-medium">Invio</th>
                  <th className="text-left px-3 py-1.5 text-muted-foreground font-medium">Risposta</th>
                  <th className="text-left px-3 py-1.5 text-muted-foreground font-medium">Preventivo</th>
                  <th className="px-3 py-1.5 w-40"></th>
                </tr>
              </thead>
              <tbody>
                {richiesta.invii.map((invio) => {
                  const badge = STATI_INVIO_BADGE[invio.stato_invio] ?? {
                    label: invio.stato_invio,
                    className: 'bg-muted text-muted-foreground',
                  };
                  return (
                    <tr key={invio.id} className="border-b border-border">
                      <td className="px-3 py-1.5">{fornitoreName(invio.fornitore_id)}</td>
                      <td className="px-3 py-1.5 text-muted-foreground text-xs">
                        {invio.email_destinatario || '—'}
                      </td>
                      <td className="px-3 py-1.5">
                        <span
                          className={`inline-flex items-center px-2 py-0.5 rounded text-xs font-medium ${badge.className}`}
                        >
                          {badge.label}
                        </span>
                      </td>
                      <td className="px-3 py-1.5">
                        {invio.data_invio ? date(invio.data_invio) : '—'}
                      </td>
                      <td className="px-3 py-1.5">
                        {invio.data_risposta ? date(invio.data_risposta) : '—'}
                      </td>
                      <td className="px-3 py-1.5">
                        {invio.preventivo_id ? (
                          <Button
                            variant="link"
                            size="sm"
                            className="text-xs p-0 h-auto"
                            onClick={() => navigate(`/preventivi/${invio.preventivo_id}`)}
                          >
                            Vedi preventivo
                          </Button>
                        ) : (
                          '—'
                        )}
                      </td>
                      <td className="px-3 py-1.5 text-right">
                        <div className="flex gap-1 justify-end">
                          {!invio.preventivo_id && invio.stato_invio !== 'risposta' && (
                            <Button
                              variant="outline"
                              size="sm"
                              onClick={() => handleSollecita(invio.id)}
                              disabled={actionLoading}
                              className="text-xs"
                            >
                              Sollecita
                            </Button>
                          )}
                          {!invio.preventivo_id && (
                            <Button
                              variant="outline"
                              size="sm"
                              onClick={() => handleOpenCollega(invio)}
                              disabled={actionLoading}
                              className="text-xs"
                            >
                              Collega
                            </Button>
                          )}
                        </div>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        )}

        {/* Collega preventivo dialog inline */}
        {collegaInvioId && (
          <div className="border border-accent/30 rounded p-3 space-y-2 bg-muted/20">
            <h3 className="text-sm font-medium text-foreground">Collega preventivo</h3>
            <div className="flex gap-2 items-end">
              <div className="flex-1">
                <Label htmlFor="collega_preventivo">Preventivo</Label>
                <select
                  id="collega_preventivo"
                  value={collegaPreventivoId}
                  onChange={(e) => setCollegaPreventivoId(e.target.value)}
                  className="w-full mt-1 rounded border border-border bg-card text-foreground px-3 py-2 text-sm"
                >
                  <option value="">Seleziona...</option>
                  {preventiviDisponibili.map((p) => (
                    <option key={p.id} value={p.id}>
                      {p.numero} — {date(p.data_preventivo)} ({p.stato})
                    </option>
                  ))}
                </select>
              </div>
              <Button
                onClick={handleCollegaPreventivo}
                disabled={actionLoading || !collegaPreventivoId}
                size="sm"
              >
                Collega
              </Button>
              <Button
                variant="outline"
                size="sm"
                onClick={() => {
                  setCollegaInvioId(null);
                  setCollegaPreventivoId('');
                  setPreventiviDisponibili([]);
                }}
              >
                Annulla
              </Button>
            </div>
          </div>
        )}

        {/* Form invio a nuovo fornitore */}
        {isEditable && (
          <div className="border-t border-border pt-3 space-y-2">
            <h3 className="text-sm font-medium text-foreground">Invia a fornitore</h3>
            <div className="flex gap-2 items-end">
              <div className="flex-1">
                <Label htmlFor="invio_fornitore">Fornitore *</Label>
                <select
                  id="invio_fornitore"
                  value={invioFornitoreId}
                  onChange={(e) => setInvioFornitoreId(e.target.value)}
                  className="w-full mt-1 rounded border border-border bg-card text-foreground px-3 py-2 text-sm"
                >
                  <option value="">Seleziona...</option>
                  {fornitori.map((f) => (
                    <option key={f.id} value={f.id}>
                      {nomeFornitore(f)}
                    </option>
                  ))}
                </select>
              </div>
              <div className="flex-1">
                <Label htmlFor="invio_email">Email (opzionale)</Label>
                <Input
                  id="invio_email"
                  type="email"
                  value={invioEmail}
                  onChange={(e) => setInvioEmail(e.target.value)}
                  className="mt-1"
                  placeholder="email@fornitore.it"
                />
              </div>
              <Button
                onClick={handleInviaAFornitore}
                disabled={actionLoading || !invioFornitoreId}
                size="sm"
              >
                Invia
              </Button>
            </div>
          </div>
        )}
      </Card>

      {/* Confronto preventivi */}
      {showConfronto && <ConfrontoPreventiviPanel richiestaId={richiesta.id} />}
    </div>
  );
}
